import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/label';
import { ESymbol } from '../../pdf/draw/symbol';

/**
 * Gluckhaus (Luck House) - Medieval German board game
 *
 * Layout: 1 portrait page, 3x7 grid.
 * Roof triangles at key positions, numbered cells with images,
 * special cells for Pig (2), King (12), and Wedding (7).
 *
 * Migrated from C# EduFrame.Img10.Generators.Gluckhaus
 */
export class Gluckhaus implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(15);
    const { origin, size } = calcOrigin(3, 7, margin, PdfSize.A4);

    const orangeRed = new PdfBrush(PdfColor.fromRgb(255, 69, 0));

    const page = pdf.addPage();

    const R = (x: number, y: number) =>
      new PdfRect(origin.x + size.width * x, origin.y + size.height * y, size.width, size.height);

    const drawRoof = (x: number, y: number) => {
      const r = R(x, y);
      page.drawPolygon(null, orangeRed, [
        r.bottomLeft,
        r.bottomRight,
        new PdfPoint(r.centerX, r.centerY),
      ]);
    };

    const drawNumber = (x: number, y: number, number: number, index: number) => {
      const r = R(x, y);
      if (index < opts.list.length) {
        page.drawImageCenter(opts.list[index].image, r);
      }
      page.drawString(`${number}`, GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.TopRight);
      page.drawRectangle(GUtil.basePen, null, r);
    };

    // Roofs
    drawRoof(1, 0);
    drawRoof(0, 1);
    drawRoof(2, 1);
    drawRoof(0, 3);
    drawRoof(2, 3);

    // Number cells: (col, row, display_number, image_index)
    drawNumber(2, 2, 3, 0);
    drawNumber(0, 2, 11, 1);
    drawNumber(2, 4, 5, 2);
    drawNumber(0, 4, 6, 3);
    drawNumber(1, 3, 10, 4);
    drawNumber(1, 4, 9, 5);
    drawNumber(1, 5, 8, 6);

    // Pig cell (double-wide, index 7)
    {
      const r = new PdfRect(
        origin.x + size.width * 0.5,
        origin.y + size.height * 6,
        size.width * 2,
        size.height
      );
      if (7 < opts.list.length) {
        page.drawImageCenter(opts.list[7].image, r);
      }
      page.drawSymbol(ESymbol.Pig, PdfBrush.Black, r, 48, ETextAlign.TopLeft);
      page.drawString('2', GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.TopRight);
      page.drawRectangle(GUtil.basePen, null, r);
    }

    // King cell (index 8)
    {
      const r = R(1, 1);
      if (8 < opts.list.length) {
        page.drawImageCenter(opts.list[8].image, r);
      }
      page.drawSymbol(ESymbol.Crown, PdfBrush.Black, r, 48, ETextAlign.TopLeft);
      page.drawString('12', GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.TopRight);
      page.drawRectangle(GUtil.basePen, null, r);
    }

    // Wedding cell (index 9)
    {
      const r = R(1, 2);
      if (9 < opts.list.length) {
        page.drawImageCenter(opts.list[9].image, r);
      }
      page.drawSymbol(ESymbol.Rings, PdfBrush.Black, r, 48, ETextAlign.TopLeft);
      page.drawString('7', GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.TopRight);
      page.drawRectangle(GUtil.basePen, null, r);
    }
  }
}
